import { RecoveryClient } from "#root/bin/recoveryserver/client";
import { sha1_LEGACY_DO_NOT_USE } from "#root/lib/crypto/hash";
import { OPAQUE } from "#root/lib/crypto/opaque";
import type { OPRF } from "#root/lib/crypto/oprf";
import { Secretbox } from "#root/lib/crypto/secretbox.js";
import { BaseService } from "#root/proto/base_pb";
import {
  UserService,
  VerifyEmailResponse,
  type VerifyEmailInner,
} from "#root/proto/user_pb";
import type { JsonObject } from "@bufbuild/protobuf";
import sodium from "libsodium-wrappers-sumo";
import { ClientBase } from "./base";
import { endpoints as v1 } from "./endpoints";

/**
 * This salt is used for OPAQUE logins. Originally we were going to provide a different salt per-environment, but
 * this never ended up happening. Existing code depends on it, though, so we keep it around.
 */
const BADGE_SALT = "0ddMh4J8gWtnC4Gt";

export class AllAccountsClient extends ClientBase {
  constructor(baseURL = "") {
    super(baseURL);

    // Always fetch the current flags at startup so that derived classes can use them.
    const flagsClient = this.useClient(BaseService);
    this.flagsFetched = flagsClient.getFlags({}).then((value) => {
      this.cachedFlags = new Map(Object.entries(value.flags));
    });
  }

  // Flags /////////////////////////////////////////////////////////////////////
  protected flagsFetched: Promise<void>;
  private cachedFlags: Map<string, string>;

  async getFlag(key: string): Promise<string> {
    await this.flagsFetched;
    return this.cachedFlags.get(key);
  }

  // User Tracking /////////////////////////////////////////////////////////////
  getMarker(): string {
    return this.credentials.get().userID;
  }

  // Account Recovery //////////////////////////////////////////////////////////
  async getRecoveryClient(): Promise<RecoveryClient> {
    return new RecoveryClient("/alpha", "/beta");
  }

  // Accounts //////////////////////////////////////////////////////////////////
  async createAccount(
    username: string,
    password: string,
    token: string,
    privacyPolicyAccepted: boolean,
    campusName?: string,
    emailDomain?: string,
  ): Promise<void> {
    await sodium.ready;

    // Make the username lowercase always, to standardize on something.
    username = username.toLowerCase();

    const usernameHash = OPAQUE.makeUsername(username);
    const passwordHash = OPAQUE.makePassword(password, BADGE_SALT);

    const keys = OPAQUE.generateKeys();
    const alpha = OPAQUE.mask(passwordHash);

    const oprfData = await this.call(v1.CreateAccount_Step1_OPRF)({
      token,
      index: usernameHash,
      alpha: alpha.point,
      userPublicKey: keys.pk,
      privacyPolicyAccepted,
      campusName,
      emailDomain,
    });

    const unmasked = OPAQUE.unmask(oprfData.beta, alpha.mask);
    const encrypted = OPAQUE.encrypt(unmasked, keys);

    // Set up the necessary keys and data for user accounts.
    this.username = username;
    this.userID = oprfData.userID;
    this.serverKey = oprfData.serverPublicKey;
    this.publicKey = keys.pk;
    this.privateKey = keys.sk;

    try {
      await this.encryptedCall(v1.CreateAccount_Step2_Finalize)({
        envelope: encrypted,
      });
    } catch (error) {
      // TODO: logging
      try {
        await this.encryptedCall(v1.UndoCreateAccountStep1)({});
      } catch (err) {
        // TODO: logging
        throw new Error(
          "Error creating account. Please start over by verifying your email address",
        );
      } finally {
        await this.logout();
      }
      throw new Error("Error creating account. Please try submitting again.");
    }
  }
  public async login(username: string, password: string): Promise<void> {
    await sodium.ready;

    const lowercaseUsername = username.toLowerCase();
    // Try using just the username as the index first.
    // This modification was made to make our instance of OPAQUE more secure
    // against dictionary attacks than the original was.
    const usernameHash = OPAQUE.makeUsername(lowercaseUsername);
    const passwordHash = OPAQUE.makePassword(password, BADGE_SALT);
    const alpha = OPAQUE.mask(passwordHash);

    try {
      await this.doLogin(lowercaseUsername, usernameHash, alpha);
    } catch (_err) {
      console.log(_err);
      try {
        // Try to use a lowercase username first, since that will be the case
        // for the majority of users.
        const identity = OPAQUE.makeIdentity(
          lowercaseUsername,
          password,
          BADGE_SALT,
        );
        const alpha1 = OPAQUE.mask(identity.key);
        await this.doLogin(lowercaseUsername, identity.index, alpha1);
      } catch (err) {
        if (lowercaseUsername === username) {
          throw err;
        }
        // Failing that, try the original provided username.
        const identity = OPAQUE.makeIdentity(username, password, BADGE_SALT);
        const alpha1 = OPAQUE.mask(identity.key);
        await this.doLogin(username, identity.index, alpha1);
      }

      // We need to upgrade the index to match the new method so we can skip
      // these old index styles.
      const { output: beta } = await this.encryptedCall(v1.OPRF_Me)({
        alpha: alpha.point,
      });
      const unmasked = OPAQUE.unmask(beta, alpha.mask);
      const encrypted = OPAQUE.encrypt(unmasked, {
        pk: this.publicKey,
        sk: this.privateKey,
      });

      await this.encryptedCall(v1.UpdatePassword)({
        newIndex: usernameHash,
        newEnvelope: encrypted,
      });
    }
  }

  private async doLogin(username: string, index: string, alpha: OPRF.Alpha) {
    const oprfData = await this.call(v1.Login)({
      index,
      alpha: alpha.point,
      passwordCheck: false,
    });

    const unmasked = OPAQUE.unmask(oprfData.beta, alpha.mask);
    const decrypted = OPAQUE.decrypt(unmasked, oprfData.encryptedEnvelope);

    this.username = username;
    this.userID = oprfData.userID;
    this.serverKey = oprfData.serverPublicKey;
    this.publicKey = decrypted.pk;
    this.privateKey = decrypted.sk;
  }

  async logout(): Promise<void> {
    // Calling the service to delete the CALLISTO_AUTH token is now necessary, since client-side JS can't do it.
    const client = this.useClient(UserService);
    await client.logout({});

    this.credentials.reset();
  }

  async checkPasswordStrength(password: string): Promise<void> {
    if (password.length < 8) {
      return Promise.reject();
    }

    const hash = sha1_LEGACY_DO_NOT_USE(password);
    const prefix = hash.substring(0, 5);
    const { hashes } = await this.call(v1.CheckPasswordStrength)({ prefix });

    if (hashes.some((v) => v === hash)) {
      return Promise.reject();
    }

    return Promise.resolve();
  }

  // Verification //////////////////////////////////////////////////////////////
  /**
   * Begin the process of partner verification.
   * @returns An encrypted blob, keyed to a PIN generated by the server and sent via a second factor.
   */
  public async verifyPartnerAffiliation_Step_1_Start(
    givenEmail: string,
  ): Promise<VerifyEmailResponse> {
    const client = this.useClient(UserService);
    return await client.verifyEmail({ givenEmail });
  }

  /**
   * Decrypts the token needed for verification using the PIN received on a second factor.
   */
  public async verifyPartnerAffiliation_Step_2_Decrypt(
    encrypted: Uint8Array,
    givenPin: string,
  ): Promise<string> {
    const actualKey = sodium.crypto_hash_sha256(givenPin);
    const decrypted: VerifyEmailInner = Secretbox.decrypt(encrypted, actualKey);
    return decrypted.givenEmail;
  }

  // Surveys ///////////////////////////////////////////////////////////////////
  public async submitSurvey(
    pk: Uint8Array,
    input: JsonObject,
    kind: string,
    linkedCampus: string,
    linkedEntry: string,
  ): Promise<string> {
    const client = this.useClient(UserService);
    const resp = await client.respondToSurvey({
      pk,
      responses: input,
      kind,
      linkedCampus,
      linkedEntry,
    });

    return resp.surveyID;
  }
}
